import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  CheckPicker,
  Input,
  Menu,
  Message,
  Popover,
  SelectPicker,
  Stack,
  toaster,
  Whisper,
} from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useAddActivitySupporter,
  useActivityHourForecastTotals,
  useCreateActivity,
  useDeleteTask,
  useDeleteActivity,
  useProjectActivityForecastSummary,
  useProjectUsers,
  useSetActivitySupporters,
  useShiftTaskHourForecast,
  useTaskOdcActivityTotals,
  useTaskHourForecast,
  useTaskOdcMonthlyTotals,
  useTask,
  useTaskOrders,
  useTasks,
  useUpdateTask,
  useUpdateActivity,
  useUsers,
} from '../hooks/useQueries';

import { useWbsTemplateItems, useWbsTemplates } from '../hooks/useWbs';

import { clearRsuitePickerSearchInputSoon } from '../utils/rsuitePickerSearch';
import usePermissions from '../hooks/usePermissions';
import './TaskDetailPage.css';


// The supports column is too narrow for full names, so collapsed chips and the picker
// toggle show only the given name ("Annie Parham" -> "Annie") with the full name on hover.
function firstNameOf(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

function ActivitySupportHoursExpanded({ taskId, activityId }) {
  const {
    data: totals,
    isLoading,
    error,
  } = useActivityHourForecastTotals(
    Number.isInteger(taskId) ? taskId : null,
    Number.isInteger(activityId) ? activityId : null,
    { enabled: Number.isInteger(taskId) && Number.isInteger(activityId) }
  );

  const peopleTotals = useMemo(() => {
    const rows = Array.isArray(totals?.rows) ? totals.rows : [];

    const mapped = rows
      .filter((r) => Number.isInteger(Number(r?.userId)))
      .map((r) => ({
        userId: Number(r.userId),
        userName: r?.userName || '—',
        role: r?.role || 'support',
        totalHours: Number(r?.totalHours ?? 0),
      }));

    mapped.sort((a, b) => {
      const aLead = a.role === 'lead' ? 0 : 1;
      const bLead = b.role === 'lead' ? 0 : 1;
      if (aLead !== bLead) return aLead - bLead;
      return String(a.userName).localeCompare(String(b.userName));
    });

    return mapped;
  }, [totals?.rows]);

  if (isLoading) {
    return <div className="ppc-dd-support__note">Loading supporter hours…</div>;
  }

  if (error) {
    return (
      <div className="ppc-dd-support__note">
        <Message type="error" showIcon>
          Failed to load supporter hours.
        </Message>
      </div>
    );
  }

  if (peopleTotals.length === 0) {
    return <div className="ppc-dd-support__note">No forecast roster found for this activity.</div>;
  }

  const totalHoursAll = peopleTotals.reduce((sum, p) => sum + Number(p?.totalHours ?? 0), 0);

  return (
    <div className="ppc-dd-support">
      <h4 className="ppc-dd-support__title">Support hours</h4>

      <div className="ppc-dd-support__grid">
        {peopleTotals.map((p) => (
          <div key={p.userId} className="ppc-dd-support__person">
            <span className="ppc-dd-support__name" title={p.userName}>
              {p.userName}
              {p.role === 'lead' ? <span className="ppc-dd-support__lead">Lead</span> : null}
            </span>
            <span className="ppc-dd-support__hours ppc-dd-num">{`${p.totalHours.toFixed(2)}h`}</span>
          </div>
        ))}
      </div>

      <div className="ppc-dd-support__total">
        <span className="ppc-dd-support__total-value ppc-dd-num">{`${Number(totalHoursAll).toFixed(2)}h`}</span>
        <span className="ppc-dd-support__total-label">Total</span>
      </div>
    </div>
  );
}

export default function TaskDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const taskId = Number(params?.taskId);
  const backToPath = useMemo(() => {
    const raw = location?.state?.backTo;
    if (typeof raw !== 'string') return null;
    if (!raw.startsWith('/')) return null;
    return raw;
  }, [location?.state]);

  const { data: task, isLoading, error } = useTask(Number.isInteger(taskId) ? taskId : null);

  const projectId = Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null;

  // Disciplines and their activities need Manage Disciplines on the project; shifting forecast
  // hours needs Forecast hours.
  const permissions = usePermissions();
  const canManageDisciplines = permissions.canProject(projectId, 'disciplines');
  const canForecast = permissions.canProject(projectId, 'disciplines');
  const taskOrderId = Number.isInteger(Number(task?.taskOrderId)) ? Number(task.taskOrderId) : null;
  const taskOrderPath = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    if (!Number.isInteger(taskOrderId)) return null;
    return `/projects/${projectId}/task-orders/${taskOrderId}`;
  }, [projectId, taskOrderId]);
  const taskBasePath = useMemo(() => {
    if (taskOrderPath && Number.isInteger(taskId)) return `${taskOrderPath}/tasks/${taskId}`;
    if (Number.isInteger(taskId)) return `/tasks/${taskId}`;
    return null;
  }, [taskOrderPath, taskId]);
  const taskTypeNormalized = String(task?.taskType || 'labor').toLowerCase();
  const isOdcTask = taskTypeNormalized === 'odc';
  const activityEntitySingular = isOdcTask ? 'Item' : 'Activity';
  const activityEntitySingularLower = isOdcTask ? 'item' : 'activity';
  const activityEntityPlural = isOdcTask ? 'Items' : 'Activities';

  const navigateToForecast = useCallback(
    (activityId) => {
      const aid = Number(activityId);
      if (!Number.isInteger(taskId)) return;

      if (isOdcTask) {
        const basePath = taskBasePath ? `${taskBasePath}/odc-forecast` : `/tasks/${taskId}/odc-forecast`;
        if (Number.isInteger(aid)) {
          navigate(`${basePath}?itemId=${aid}`);
          return;
        }
        navigate(basePath);
        return;
      }

      if (!Number.isInteger(aid)) return;
      if (taskBasePath) navigate(`${taskBasePath}/activities/${aid}/forecast`);
      else navigate(`/tasks/${taskId}/activities/${aid}/forecast`);
    },
    [isOdcTask, navigate, taskBasePath, taskId]
  );
  const { data: activitySummary } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const {
    data: users = [],
  } = useUsers();

  const {
    data: projectUsers = [],
    isLoading: projectUsersLoading,
    error: projectUsersError,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const {
    data: projectTasks = [],
    isLoading: projectTasksLoading,
    error: projectTasksError,
  } = useTasks({ projectId }, { enabled: Number.isInteger(projectId) });

  const { data: taskOrders = [] } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const selectedTaskOrder = useMemo(() => {
    const rows = Array.isArray(taskOrders) ? taskOrders : [];
    const id = Number(taskOrderId);
    if (!Number.isInteger(id)) return null;
    return rows.find((row) => Number(row?.id) === id) || null;
  }, [taskOrders, taskOrderId]);

  const { data: odcMonthlyTotals } = useTaskOdcMonthlyTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: isOdcTask && Number.isInteger(taskId),
  });

  const { data: odcActivityTotals } = useTaskOdcActivityTotals(Number.isInteger(taskId) ? taskId : null, {
    enabled: isOdcTask && Number.isInteger(taskId),
  });

  const deleteTaskMutation = useDeleteTask();
  const [deleteTaskOpen, setDeleteTaskOpen] = useState(false);
  const [shiftHoursOpen, setShiftHoursOpen] = useState(false);
  const [shiftHoursDeltaMonths, setShiftHoursDeltaMonths] = useState(0);

  const shiftTaskHourForecast = useShiftTaskHourForecast();
  const updateTaskMutation = useUpdateTask();

  const [changeTaskTypeOpen, setChangeTaskTypeOpen] = useState(false);
  const [pendingTaskType, setPendingTaskType] = useState('labor');
  const [changeTaskLeadOpen, setChangeTaskLeadOpen] = useState(false);
  const [pendingTaskLeadUserId, setPendingTaskLeadUserId] = useState(null);

  const shiftPreviewStartMonth = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    return `${year}-01-01`;
  }, []);

  const { data: shiftTaskForecastPreview } = useTaskHourForecast(
    Number.isInteger(taskId) ? taskId : null,
    shiftPreviewStartMonth,
    12,
    { enabled: shiftHoursOpen && Number.isInteger(taskId) }
  );

  const submitDeleteTask = async () => {
    if (!Number.isInteger(taskId)) return;
    await deleteTaskMutation.mutateAsync(taskId);
    setDeleteTaskOpen(false);
    if (taskOrderPath) {
      navigate(taskOrderPath);
      return;
    }
    if (Number.isInteger(projectId)) {
      navigate(`/projects/${projectId}`);
      return;
    }
    navigate('/');
  };

  const shiftTaskHoursBy = async (deltaMonths) => {
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(deltaMonths) || deltaMonths === 0) return false;

    try {
      await shiftTaskHourForecast.mutateAsync({ taskId, deltaMonths });
      const directionLabel = deltaMonths < 0 ? 'earlier' : 'later';
      const monthCountLabel = Math.abs(deltaMonths) === 1 ? '1 month' : `${Math.abs(deltaMonths)} months`;
      toaster.push(
        <Message showIcon type="success" closable>
          Shifted all task projections {monthCountLabel} {directionLabel}.
        </Message>,
        { placement: 'topEnd' }
      );
      return true;
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to shift task projections: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
      return false;
    }
  };

  const openShiftHoursModal = () => {
    setShiftHoursDeltaMonths(0);
    setShiftHoursOpen(true);
  };

  const openChangeTaskTypeModal = () => {
    const currentType = String(task?.taskType || 'labor').toLowerCase();
    setPendingTaskType(currentType === 'odc' ? 'odc' : 'labor');
    setChangeTaskTypeOpen(true);
  };

  const closeChangeTaskTypeModal = () => {
    if (updateTaskMutation.isPending) return;
    setChangeTaskTypeOpen(false);
  };

  const closeChangeTaskLeadModal = () => {
    if (updateTaskMutation.isPending) return;
    setChangeTaskLeadOpen(false);
  };

  const submitChangeTaskType = async () => {
    if (!Number.isInteger(taskId)) return;
    const normalized = String(pendingTaskType || '').toLowerCase();
    if (normalized !== 'labor' && normalized !== 'odc') {
      toaster.push(
        <Message showIcon type="warning" closable>
          Task type must be Labor or ODC.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await updateTaskMutation.mutateAsync({ taskId, payload: { taskType: normalized } });
    toaster.push(
      <Message showIcon type="success" closable>
        Task type updated.
      </Message>,
      { placement: 'topEnd' }
    );
    setChangeTaskTypeOpen(false);
  };

  const submitChangeTaskLead = async () => {
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(Number(pendingTaskLeadUserId))) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a task lead.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    await updateTaskMutation.mutateAsync({ taskId, payload: { leadUserId: Number(pendingTaskLeadUserId) } });
    toaster.push(
      <Message showIcon type="success" closable>
        Task lead updated.
      </Message>,
      { placement: 'topEnd' }
    );
    setChangeTaskLeadOpen(false);
  };

  const taskLeadOptions = useMemo(
    () => (Array.isArray(projectUsers) ? projectUsers : [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value)),
    [projectUsers]
  );

  const closeShiftHoursModal = () => {
    if (shiftTaskHourForecast.isPending) return;
    setShiftHoursOpen(false);
  };

  // Runs once the dialog has faded out (onExited), so its fields don't empty while it closes.
  const resetShiftHoursModal = () => {
    setShiftHoursDeltaMonths(0);
  };

  const confirmShiftHours = async () => {
    if (!Number.isInteger(shiftHoursDeltaMonths) || shiftHoursDeltaMonths === 0) return;
    const ok = await shiftTaskHoursBy(shiftHoursDeltaMonths);
    if (ok) {
      setShiftHoursOpen(false);
      setShiftHoursDeltaMonths(0);
    }
  };

  const shiftPreviewMonthStarts = useMemo(() => {
    const months = Array.isArray(shiftTaskForecastPreview?.months) ? shiftTaskForecastPreview.months : [];
    return months
      .map((m) => String(m?.monthStart || '').slice(0, 10))
      .filter((m) => m.length === 10);
  }, [shiftTaskForecastPreview?.months]);

  const shiftPreviewBaseTotals = useMemo(() => {
    const totals = shiftTaskForecastPreview?.totals && typeof shiftTaskForecastPreview.totals === 'object'
      ? shiftTaskForecastPreview.totals
      : {};
    const map = new Map();
    for (const [monthStart, hours] of Object.entries(totals)) {
      const ms = String(monthStart || '').slice(0, 10);
      map.set(ms, Number(hours ?? 0));
    }
    return map;
  }, [shiftTaskForecastPreview?.totals]);

  const addMonthsIso = useCallback((isoDate, delta) => {
    const value = String(isoDate || '').slice(0, 10);
    if (value.length !== 10 || !Number.isInteger(delta)) return null;
    const [y, m] = value.split('-').map((n) => Number.parseInt(n, 10));
    if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    const yy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}-01`;
  }, []);

  const shiftPreviewHoursByMonth = useMemo(() => {
    const out = new Map();
    for (const monthStart of shiftPreviewMonthStarts) {
      const sourceMonth = addMonthsIso(monthStart, -shiftHoursDeltaMonths);
      out.set(monthStart, Number(shiftPreviewBaseTotals.get(sourceMonth) ?? 0));
    }
    return out;
  }, [addMonthsIso, shiftPreviewMonthStarts, shiftHoursDeltaMonths, shiftPreviewBaseTotals]);

  const shiftPreviewMonthLabelFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { month: 'short', year: '2-digit' }),
    []
  );

  const formatShiftPreviewMonthLabel = useCallback(
    (monthStart) => {
      const [y, m] = String(monthStart || '').slice(0, 10).split('-').map((n) => Number.parseInt(n, 10));
      if (!Number.isInteger(y) || !Number.isInteger(m)) return monthStart;
      return shiftPreviewMonthLabelFormatter.format(new Date(Date.UTC(y, m - 1, 1)));
    },
    [shiftPreviewMonthLabelFormatter]
  );

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      map.set(id, u?.name || '—');
    }
    return map;
  }, [users]);

  const projectMemberOptionsBase = useMemo(() => {
    return (projectUsers || [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value));
  }, [projectUsers]);

  const buildPickerData = useCallback((baseOptions, selectedIds) => {
    const data = Array.isArray(baseOptions) ? [...baseOptions] : [];
    const disabledItemValues = [];
    const present = new Set(data.map((o) => Number(o?.value)).filter((v) => Number.isInteger(v)));

    for (const rawId of Array.isArray(selectedIds) ? selectedIds : [selectedIds]) {
      const id = Number(rawId);
      if (!Number.isInteger(id)) continue;
      if (present.has(id)) continue;

      const name = userNameById.get(id);
      if (!name) continue;

      data.push({ label: `${name} (not on project)`, value: id });
      disabledItemValues.push(id);
      present.add(id);
    }

    return { data, disabledItemValues };
  }, [userNameById]);

  const activities = useMemo(() => (Array.isArray(task?.activities) ? task.activities : []), [task]);

  const { data: wbsTemplates = [], isLoading: wbsTemplatesLoading, error: wbsTemplatesError } = useWbsTemplates();
  const [importWbsOpen, setImportWbsOpen] = useState(false);
  const [importTemplateId, setImportTemplateId] = useState(null);
  const [importWbsPending, setImportWbsPending] = useState(false);
  const { data: importWbsItems = [], isLoading: importWbsItemsLoading, error: importWbsItemsError } = useWbsTemplateItems(
    importTemplateId,
    {
      enabled: importWbsOpen && Number.isInteger(Number(importTemplateId)),
    }
  );

  const forecastHoursByActivityId = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];
    const m = new Map();
    for (const a of arr) {
      const activityId = Number(a?.activityId);
      if (!Number.isInteger(activityId)) continue;
      m.set(activityId, Number(a?.totalHours ?? 0));
    }
    return m;
  }, [activitySummary?.activities]);

  const forecastCostByActivityId = useMemo(() => {
    const arr = Array.isArray(activitySummary?.activities) ? activitySummary.activities : [];
    const m = new Map();
    for (const a of arr) {
      const activityId = Number(a?.activityId);
      if (!Number.isInteger(activityId)) continue;
      m.set(activityId, Number(a?.totalCost ?? 0));
    }
    return m;
  }, [activitySummary?.activities]);

  const odcForecastByActivityId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(odcActivityTotals?.rows) ? odcActivityTotals.rows : [];
    for (const row of rows) {
      const id = Number(row?.activityId);
      if (!Number.isInteger(id)) continue;
      map.set(id, Number(row?.forecastAmount ?? 0));
    }
    return map;
  }, [odcActivityTotals?.rows]);

  const odcActualByActivityId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(odcActivityTotals?.rows) ? odcActivityTotals.rows : [];
    for (const row of rows) {
      const id = Number(row?.activityId);
      if (!Number.isInteger(id)) continue;
      map.set(id, Number(row?.actualAmount ?? 0));
    }
    return map;
  }, [odcActivityTotals?.rows]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    const n = Number(amount);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [newActivityName, setNewActivityName] = useState('');
  const [newActivityAssumptions, setNewActivityAssumptions] = useState('');
  const [newActivityLeadUserId, setNewActivityLeadUserId] = useState(null);
  const [newActivitySupportUserIds, setNewActivitySupportUserIds] = useState([]);
  const newActivitySupportsPickerRef = useRef(null);

  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [editTaskActivity, setEditTaskActivity] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskAssumptions, setEditTaskAssumptions] = useState('');
  const [editTaskLeadUserId, setEditTaskLeadUserId] = useState(null);
  const [editTaskSupportUserIds, setEditTaskSupportUserIds] = useState([]);
  const editTaskLeadPickerRef = useRef(null);
  const editTaskSupportsPickerRef = useRef(null);

  const [confirmTeamRemovalOpen, setConfirmTeamRemovalOpen] = useState(false);
  const [confirmRemovedUserIds, setConfirmRemovedUserIds] = useState([]);
  const [editTaskPendingSave, setEditTaskPendingSave] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteActivity, setDeleteActivity] = useState(null);

  const [moveActivityOpen, setMoveActivityOpen] = useState(false);
  const [moveActivity, setMoveActivity] = useState(null);
  const [moveTargetTaskId, setMoveTargetTaskId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null); // 'name' | 'lead' | 'assumptions'
  const [editName, setEditName] = useState('');
  const [editAssumptions, setEditAssumptions] = useState('');
  const [editLeadUserId, setEditLeadUserId] = useState(null);
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);
  const [editingSupportsActivityId, setEditingSupportsActivityId] = useState(null);
  const [editingSupportsUserIds, setEditingSupportsUserIds] = useState([]);
  const [supportsPickerOpen, setSupportsPickerOpen] = useState(false);

  const editNameRef = useRef(null);
  const editAssumptionsRef = useRef(null);
  const editLeadEditorRef = useRef(null);
  const editLeadUserIdRef = useRef(null);
  const editSupportsUserIdsRef = useRef([]);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const toggleExpanded = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id)) return;
    setExpandedRowKeys((prev) => {
      const has = prev.includes(id);
      if (has) return prev.filter((k) => k !== id);
      return [...prev, id];
    });
  };

  const createActivity = useCreateActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          {activityEntitySingular} created.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const createActivitySilent = useCreateActivity();

  const updateActivity = useUpdateActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          {activityEntitySingular} updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const addSupporter = useAddActivitySupporter({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Support added.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const setSupporters = useSetActivitySupporters({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          Supports updated.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const closeSupportsEdit = useCallback(() => {
    setEditingSupportsActivityId(null);
    setEditingSupportsUserIds([]);
    editSupportsUserIdsRef.current = [];
    setSupportsPickerOpen(false);
  }, []);

  const openSupportsEdit = useCallback((rowData) => {
    const activityId = Number(rowData?.id);
    if (!Number.isInteger(activityId)) return;
    const supporterIds = Array.from(
      new Set(
        (Array.isArray(rowData?.supporters) ? rowData.supporters : [])
          .map((s) => Number(s?.userId))
          .filter((id) => Number.isInteger(id))
      )
    );

    setEditingSupportsActivityId(activityId);
    setEditingSupportsUserIds(supporterIds);
    editSupportsUserIdsRef.current = supporterIds;
    setSupportsPickerOpen(true);
  }, []);

  const saveSupportsEdit = useCallback(async (overrideUserIds = null) => {
    const activityId = Number(editingSupportsActivityId);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) {
      closeSupportsEdit();
      return;
    }
    if (setSupporters.isPending) return;

    const row = (Array.isArray(activities) ? activities : [])
      .find((r) => Number(r?.id) === activityId);
    if (!row) {
      closeSupportsEdit();
      return;
    }

    const leadUserId = Number.isInteger(Number(row?.leadUserId)) ? Number(row.leadUserId) : null;
    const nextSupportIds = Array.from(
      new Set(
        (Array.isArray(overrideUserIds) ? overrideUserIds : editSupportsUserIdsRef.current)
          .map((v) => Number(v))
          .filter((v) => Number.isInteger(v))
      )
    ).filter((v) => (Number.isInteger(leadUserId) ? v !== leadUserId : true));

    const originalSupportIds = Array.from(
      new Set(
        (Array.isArray(row?.supporters) ? row.supporters : [])
          .map((s) => Number(s?.userId))
          .filter((id) => Number.isInteger(id))
      )
    ).filter((v) => (Number.isInteger(leadUserId) ? v !== leadUserId : true));

    const sameLength = nextSupportIds.length === originalSupportIds.length;
    const sameValues = sameLength && nextSupportIds.every((id) => originalSupportIds.includes(id));
    if (sameValues) {
      closeSupportsEdit();
      return;
    }

    try {
      await setSupporters.mutateAsync({
        taskId,
        activityId,
        userIds: nextSupportIds,
      });
      closeSupportsEdit();
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || err || 'Failed to update supports.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  }, [taskId, editingSupportsActivityId, setSupporters, activities, closeSupportsEdit]);

  const deleteActivityMutation = useDeleteActivity({
    onSuccess: async () => {
      toaster.push(
        <Message showIcon type="success" closable>
          {activityEntitySingular} deleted.
        </Message>,
        { placement: 'topEnd' }
      );
    },
  });

  const getDisplayNameForUserId = useCallback(
    (userId) => {
      const id = Number(userId);
      if (!Number.isInteger(id)) return '';
      const fromMap = userNameById.get(id);
      if (fromMap) return fromMap;

      // Fallback: sometimes supporters include names even if not on project.
      const supporters = Array.isArray(editTaskActivity?.supporters) ? editTaskActivity.supporters : [];
      const match = supporters.find((s) => Number(s?.userId) === id);
      return String(match?.name || `User ${id}`);
    },
    [userNameById, editTaskActivity]
  );

  const submitEditTask = async ({ skipConfirm } = {}) => {
    const activityId = Number(editTaskActivity?.id);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;

    const name = String(editTaskName || '').trim();
    const assumptions = String(editTaskAssumptions || '').trim();
    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          {activityEntitySingular} name is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const originalLeadId = Number.isInteger(Number(editTaskActivity?.leadUserId)) ? Number(editTaskActivity.leadUserId) : null;
    const originalSupportIds = (Array.isArray(editTaskActivity?.supporters) ? editTaskActivity.supporters : [])
      .map((s) => Number(s?.userId))
      .filter((id) => Number.isInteger(id));

    const originalTeam = new Set([
      ...(Number.isInteger(originalLeadId) ? [originalLeadId] : []),
      ...originalSupportIds,
    ]);

    const nextSupportIds = Array.from(
      new Set((Array.isArray(editTaskSupportUserIds) ? editTaskSupportUserIds : []).map((v) => Number(v)))
    )
      .filter((v) => Number.isInteger(v))
      .filter((v) => (Number.isInteger(leadId) ? v !== leadId : true));

    const nextTeam = new Set([...(Number.isInteger(leadId) ? [leadId] : []), ...nextSupportIds]);
    const removed = Array.from(originalTeam).filter((id) => !nextTeam.has(id));

    if (!skipConfirm && removed.length > 0) {
      setConfirmRemovedUserIds(removed);
      setConfirmTeamRemovalOpen(true);
      setEditTaskPendingSave(true);
      return;
    }

    await updateActivity.mutateAsync({
      taskId,
      activityId,
      payload: {
        name,
        assumptions,
        leadUserId: Number.isInteger(leadId) ? leadId : null,
      },
    });

    await setSupporters.mutateAsync({
      taskId,
      activityId,
      userIds: nextSupportIds,
    });

    setEditTaskOpen(false);
    setEditTaskActivity(null);
    setEditTaskName('');
    setEditTaskAssumptions('');
    setEditTaskLeadUserId(null);
    setEditTaskSupportUserIds([]);
    setConfirmTeamRemovalOpen(false);
    setConfirmRemovedUserIds([]);
    setEditTaskPendingSave(false);
  };


  const openDelete = (activity) => {
    setDeleteActivity(activity);
    setDeleteOpen(true);
  };

  const moveDisciplineOptions = useMemo(() => {
    const rows = Array.isArray(projectTasks) ? projectTasks : [];
    return rows
      .map((t) => ({
        value: Number(t?.id),
        label: String(t?.task || t?.name || `Discipline ${t?.id || ''}`).trim(),
      }))
      .filter((o) => Number.isInteger(o.value) && o.value !== Number(taskId) && o.label)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [projectTasks, taskId]);

  const forceCloseMoveActivityModal = () => {
    setMoveActivityOpen(false);
    setMoveActivity(null);
    setMoveTargetTaskId(null);
  };

  const closeMoveActivityModal = () => {
    if (updateActivity.isPending) return;
    forceCloseMoveActivityModal();
  };

  const openMoveActivityModal = (activity) => {
    const activityId = Number(activity?.id);
    if (!Number.isInteger(activityId)) return;
    setMoveActivity(activity);
    setMoveTargetTaskId(Number(moveDisciplineOptions?.[0]?.value) || null);
    setMoveActivityOpen(true);
  };

  const submitMoveActivity = async () => {
    const activityId = Number(moveActivity?.id);
    const targetTaskId = Number(moveTargetTaskId);

    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;

    if (!Number.isInteger(targetTaskId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a discipline.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (targetTaskId === Number(taskId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Select a different discipline.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    try {
      await updateActivity.mutateAsync({
        taskId,
        activityId,
        payload: {
          name: String(moveActivity?.name || '').trim(),
          assumptions: String(moveActivity?.assumptions || '').trim(),
          leadUserId: Number.isInteger(Number(moveActivity?.leadUserId)) ? Number(moveActivity.leadUserId) : null,
          newTaskId: targetTaskId,
        },
      });

      forceCloseMoveActivityModal();
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || err || `Failed to move ${activityEntitySingularLower}.`)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const submitDelete = async () => {
    const activityId = Number(deleteActivity?.id);
    if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return;
    await deleteActivityMutation.mutateAsync({ taskId, activityId });
    if (Number(editingId) === activityId) cancelEdit();
    if (Number(editingSupportsActivityId) === activityId) closeSupportsEdit();
    setDeleteOpen(false);
    setDeleteActivity(null);
  };

  const editTaskLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, editTaskLeadUserId),
    [projectMemberOptionsBase, editTaskLeadUserId, buildPickerData]
  );

  const editTaskSupportsPicker = useMemo(() => {
    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;
    const base = projectMemberOptionsBase.filter((o) => {
      const id = Number(o?.value);
      if (!Number.isInteger(id)) return false;
      if (Number.isInteger(leadId) && id === leadId) return false;
      return true;
    });
    return buildPickerData(base, editTaskSupportUserIds);
  }, [projectMemberOptionsBase, editTaskLeadUserId, editTaskSupportUserIds, buildPickerData]);

  const openAdd = () => {
    setNewActivityName('');
    setNewActivityAssumptions('');
    setNewActivityLeadUserId(null);
    setNewActivitySupportUserIds([]);
    setAddActivityOpen(true);
  };

  const openImportWbs = () => {
    const firstTemplateId = Number(wbsTemplates?.[0]?.id);
    setImportTemplateId(Number.isInteger(firstTemplateId) ? firstTemplateId : null);
    setImportWbsOpen(true);
  };

  const submitImportWbs = async () => {
    if (!Number.isInteger(taskId)) return;
    const templateId = Number(importTemplateId);
    if (!Number.isInteger(templateId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a template.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const sortedItems = (Array.isArray(importWbsItems) ? importWbsItems : [])
      .map((i) => ({
        id: Number(i?.id),
        title: String(i?.title ?? '').trim(),
        assumptions: String(i?.assumptions ?? '').trim(),
        sortOrder: Number(i?.sortOrder ?? 0),
      }))
      .filter((i) => Number.isInteger(i.id) && i.title)
      .sort((a, b) => {
        const so = a.sortOrder - b.sortOrder;
        if (so !== 0) return so;
        return a.id - b.id;
      });

    if (sortedItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Selected template has no items.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setImportWbsPending(true);
    try {
      for (const item of sortedItems) {
        // eslint-disable-next-line no-await-in-loop
        await createActivitySilent.mutateAsync({
          taskId,
          payload: { name: item.title, assumptions: item.assumptions },
        });
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Imported {sortedItems.length} {isOdcTask ? 'items' : 'activities'}.
        </Message>,
        { placement: 'topEnd' }
      );

      setImportWbsOpen(false);
      setImportTemplateId(null);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Import failed.')}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setImportWbsPending(false);
    }
  };

  const submitAdd = async () => {
    const name = String(newActivityName || '').trim();
    const assumptions = String(newActivityAssumptions || '').trim();

    if (!Number.isInteger(taskId)) {
      toaster.push(
        <Message showIcon type="error" closable>
          Invalid task.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter an {activityEntitySingularLower} name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const created = await createActivity.mutateAsync({
      taskId,
      payload: {
        name,
        assumptions,
        leadUserId: Number.isInteger(newActivityLeadUserId) ? newActivityLeadUserId : null,
      },
    });

    const createdId = Number(created?.id);
    if (Number.isInteger(createdId)) {
      const leadId = Number.isInteger(newActivityLeadUserId) ? Number(newActivityLeadUserId) : null;
      const uniq = Array.from(
        new Set((Array.isArray(newActivitySupportUserIds) ? newActivitySupportUserIds : []).map((v) => Number(v)))
      )
        .filter((v) => Number.isInteger(v))
        .filter((v) => (Number.isInteger(leadId) ? v !== leadId : true));

      for (const userId of uniq) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await addSupporter.mutateAsync({ taskId, activityId: createdId, userId });
        } catch (e) {
          toaster.push(
            <Message showIcon type="error" closable>
              {String(e?.message || e || 'Failed to add support.')}
            </Message>,
            { placement: 'topEnd' }
          );
          break;
        }
      }
    }

    setAddActivityOpen(false);
  };

  const startEditField = (row, field) => {
    const id = Number(row?.id);
    if (!Number.isInteger(id)) return;

    const nextLeadUserId = Number.isInteger(Number(row?.leadUserId)) ? Number(row.leadUserId) : null;

    if (id !== Number(editingId)) {
      setEditingId(id);
      setEditName(String(row?.name ?? ''));
      setEditAssumptions(String(row?.assumptions ?? ''));
      setEditLeadUserId(nextLeadUserId);
    }
    editLeadUserIdRef.current = nextLeadUserId;
    setEditingField(field);
    setLeadPickerOpen(field === 'lead');
  };

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingField(null);
    setEditName('');
    setEditAssumptions('');
    setEditLeadUserId(null);
    editLeadUserIdRef.current = null;
    setLeadPickerOpen(false);
  }, []);

  const editLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, editLeadUserId),
    [projectMemberOptionsBase, editLeadUserId, buildPickerData]
  );

  const newActivityLeadPicker = useMemo(
    () => buildPickerData(projectMemberOptionsBase, newActivityLeadUserId),
    [projectMemberOptionsBase, newActivityLeadUserId, buildPickerData]
  );

  const newActivitySupportsPicker = useMemo(() => {
    const leadId = Number.isInteger(newActivityLeadUserId) ? Number(newActivityLeadUserId) : null;
    const base = projectMemberOptionsBase.filter((o) => {
      const id = Number(o?.value);
      if (!Number.isInteger(id)) return false;
      if (Number.isInteger(leadId) && id === leadId) return false;
      return true;
    });
    return buildPickerData(base, newActivitySupportUserIds);
  }, [projectMemberOptionsBase, newActivityLeadUserId, newActivitySupportUserIds, buildPickerData]);

  const saveEdit = useCallback(async (overrides = {}) => {
    if (!Number.isInteger(taskId) || !Number.isInteger(editingId)) return;
    if (updateActivity.isPending) return;

    const originalActivity = (Array.isArray(activities) ? activities : [])
      .find((row) => Number(row?.id) === Number(editingId));

    const hasOverride = (key) => Object.prototype.hasOwnProperty.call(overrides, key);
    const nextNameRaw = hasOverride('name')
      ? overrides.name
      : (editNameRef.current?.value ?? editName);
    const nextAssumptionsRaw = hasOverride('assumptions')
      ? overrides.assumptions
      : (editAssumptionsRef.current?.value ?? editAssumptions);
    const nextLeadRaw = hasOverride('leadUserId') ? overrides.leadUserId : editLeadUserIdRef.current;

    const name = String(nextNameRaw || '').trim();
    const assumptions = String(nextAssumptionsRaw || '').trim();
    const nextLeadUserId = Number.isInteger(nextLeadRaw) ? Number(nextLeadRaw) : null;

    const originalName = String(originalActivity?.name ?? '').trim();
    const originalAssumptions = String(originalActivity?.assumptions ?? '').trim();
    const originalLeadUserId = Number.isInteger(Number(originalActivity?.leadUserId))
      ? Number(originalActivity.leadUserId)
      : null;

    if (
      originalActivity
      && name === originalName
      && assumptions === originalAssumptions
      && nextLeadUserId === originalLeadUserId
    ) {
      cancelEdit();
      return;
    }

    if (!name) {
      toaster.push(
        <Message showIcon type="warning" closable>
          {activityEntitySingular} name is required.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    try {
      await updateActivity.mutateAsync({
        taskId,
        activityId: editingId,
        payload: {
          name,
          assumptions,
          leadUserId: nextLeadUserId,
        },
      });

      cancelEdit();
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || err || `Failed to update ${activityEntitySingularLower}.`)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  }, [
    taskId,
    editingId,
    updateActivity,
    activities,
    editName,
    editAssumptions,
    activityEntitySingular,
    activityEntitySingularLower,
    cancelEdit,
  ]);

  useEffect(() => {
    if (!Number.isInteger(editingId)) return;
    const moveCaretToEnd = (el) => {
      if (!el || typeof el.focus !== 'function') return;
      const value = String(el.value ?? '');
      const len = value.length;
      el.focus();
      if (typeof el.setSelectionRange === 'function') {
        el.setSelectionRange(len, len);
      }
    };

    if (editingField === 'name') {
      queueMicrotask(() => moveCaretToEnd(editNameRef.current));
    }
    if (editingField === 'assumptions') {
      queueMicrotask(() => moveCaretToEnd(editAssumptionsRef.current));
    }
  }, [editingId, editingField]);

  useEffect(() => {
    if (!Number.isInteger(editingId) || editingField !== 'lead') return undefined;

    const handleOutsidePointerDown = (event) => {
      const target = event?.target;
      if (target?.closest?.('.rs-picker-popup, .rs-picker-menu, .rs-picker-select-menu')) return;
      const root = editLeadEditorRef.current;
      if (!root) return;
      if (root.contains(target)) return;
      void saveEdit();
    };

    document.addEventListener('mousedown', handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown, true);
    };
  }, [editingId, editingField, saveEdit]);

  const totalForecastHours = useMemo(() => {
    return activities.reduce((sum, a) => {
      const id = Number(a?.id);
      if (!Number.isInteger(id)) return sum;
      return sum + Number(forecastHoursByActivityId.get(id) ?? 0);
    }, 0);
  }, [activities, forecastHoursByActivityId]);

  const projectedRevenue = useMemo(() => {
    if (isOdcTask) {
      const totalFromActivities = activities.reduce((sum, a) => {
        const id = Number(a?.id);
        if (!Number.isInteger(id)) return sum;
        return sum + Number(odcForecastByActivityId.get(id) ?? 0);
      }, 0);

      const total = totalFromActivities > 0
        ? totalFromActivities
        : Number(odcMonthlyTotals?.forecastAmount ?? 0);
      return Number.isFinite(total) ? total : 0;
    }

    return activities.reduce((sum, a) => {
      const id = Number(a?.id);
      if (!Number.isInteger(id)) return sum;
      return sum + Number(forecastCostByActivityId.get(id) ?? 0);
    }, 0);
  }, [activities, forecastCostByActivityId, isOdcTask, odcForecastByActivityId, odcMonthlyTotals?.forecastAmount]);

  const actualRevenue = useMemo(() => {
    if (isOdcTask) {
      const totalFromActivities = activities.reduce((sum, a) => {
        const id = Number(a?.id);
        if (!Number.isInteger(id)) return sum;
        return sum + Number(odcActualByActivityId.get(id) ?? 0);
      }, 0);

      const fallbackTotal = Number(odcMonthlyTotals?.actualAmount ?? 0);
      const total = odcActualByActivityId.size > 0
        ? Number(totalFromActivities ?? 0)
        : fallbackTotal;
      return Number.isFinite(total) ? total : 0;
    }

    return 0;
  }, [activities, isOdcTask, odcActualByActivityId, odcMonthlyTotals?.actualAmount]);

  const allocatedBudget = useMemo(() => {
    const raw = task?.budgetTarget ?? task?.budget_target ?? null;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [task?.budgetTarget, task?.budget_target]);

  // Allocated budget is a dollar figure, so the forecast hours are compared at their projected cost.
  const budgetVariance = useMemo(() => {
    if (allocatedBudget === null) return null;
    const projected = Number(projectedRevenue);
    if (!Number.isFinite(projected)) return null;

    const delta = projected - allocatedBudget;
    return {
      projected,
      delta,
      isOver: delta > 0,
      percent: allocatedBudget > 0 ? (Math.abs(delta) / allocatedBudget) * 100 : null,
    };
  }, [allocatedBudget, projectedRevenue]);

  const getAdaptiveFontSize = (text) => {
    const len = String(text || '').trim().length;
    if (len >= 180) return 11;
    if (len >= 90) return 12;
    return 13;
  };

  // Column count for the full-width expanded row: #, activity, assumptions,
  // forecast, % of discipline, actions — plus lead/supports on labor disciplines,
  // or the actual-dollars column on ODC ones.
  const columnCount = 6 + (isOdcTask ? 1 : 2);

  const forecastTotalLabel = isOdcTask ? 'Total Forecast ($)' : 'Total Forecast (h)';
  const disciplineTotal = Number(isOdcTask ? projectedRevenue : totalForecastHours);
  const hasDisciplineTotal = Number.isFinite(disciplineTotal) && disciplineTotal > 0;

  // Progress bar for the budget card: forecast consumed out of the allocation,
  // capped at the full track so an overrun paints it solid rather than overflowing.
  const budgetUsedPercent =
    budgetVariance && allocatedBudget > 0 ? (budgetVariance.projected / allocatedBudget) * 100 : 0;
  const budgetBarPercent = Math.min(100, Math.max(0, budgetUsedPercent));

  return (
    <div className="ppc-page ppc-dd">
      <header className="ppc-dd__header">
        <div style={{ minWidth: 0 }}>
          <div className="ppc-dd__crumbs">
            <button
              type="button"
              className="ppc-dd__crumb-link"
              onClick={() => navigate('/')}
            >
              Projects
            </button>

            <i className="bi bi-chevron-right ppc-dd__crumb-sep" aria-hidden="true" />

            <button
              type="button"
              className="ppc-dd__crumb-link"
              onClick={() => {
                if (Number.isInteger(projectId)) navigate(`/projects/${projectId}`);
              }}
              disabled={!Number.isInteger(projectId)}
            >
              {task?.projectName || 'Project'}
            </button>

            <i className="bi bi-chevron-right ppc-dd__crumb-sep" aria-hidden="true" />

            {Number.isInteger(taskOrderId) ? (
              <button
                type="button"
                className="ppc-dd__crumb-link"
                onClick={() => {
                  if (taskOrderPath) navigate(taskOrderPath);
                }}
              >
                {selectedTaskOrder?.name || `Task Order ${taskOrderId}`}
              </button>
            ) : (
              <span className="ppc-dd__crumb-link">Task Order</span>
            )}

            <i className="bi bi-chevron-right ppc-dd__crumb-sep" aria-hidden="true" />
            <span className="ppc-dd__crumb-current">{task?.task || 'Discipline'}</span>
          </div>

          <h1 className="ppc-dd__title">
            <span className="ppc-dd__title-row">
              <i className="bi bi-diagram-3 ppc-dd__title-icon" aria-hidden="true" />
              <span>Discipline Details</span>
            </span>
          </h1>
        </div>

        <div className="ppc-dd__actions">
          <button
            type="button"
            className="ppc-dd-btn"
            onClick={() => {
              if (backToPath) {
                navigate(backToPath);
                return;
              }
              if (taskOrderPath) {
                navigate(taskOrderPath);
                return;
              }
              const pid = Number(task?.projectId ?? task?.project_id);
              if (Number.isInteger(pid) && pid > 0) navigate(`/projects/${pid}`);
              else navigate('/');
            }}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back
          </button>

          {/* Sits beside Back like the task order breakdown's Actuals button, rather
              than among the activity-table tools it has nothing to do with. */}
          {!isOdcTask ? (
            <button
              type="button"
              className="ppc-dd-btn"
              disabled={!Number.isInteger(taskId)}
              onClick={() => {
                if (!Number.isInteger(taskId)) return;
                if (taskBasePath) navigate(`${taskBasePath}/actuals`);
                else navigate(`/tasks/${taskId}/actuals`);
              }}
            >
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </header>

      {error ? <Message type="error" showIcon>Failed to load task.</Message> : null}

      <section className="ppc-dd-card">
        <div className="ppc-dd-card__head">
          <h2 className="ppc-dd-card__title">
            {`${activityEntityPlural} for discipline: ${task?.task ?? (isLoading ? 'Loading…' : '—')}`}
          </h2>

          <div className="ppc-dd-card__tools">
            {canManageDisciplines ? (
              <button
                type="button"
                className="ppc-dd-btn ppc-dd-btn--sm"
                onClick={openChangeTaskTypeModal}
                disabled={!Number.isInteger(taskId) || updateTaskMutation.isPending}
              >
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                Change Type
              </button>
            ) : null}

            {activities.length === 0 ? (
              canManageDisciplines ? (
                <button
                  type="button"
                  className="ppc-dd-btn ppc-dd-btn--sm"
                  onClick={openImportWbs}
                  disabled={!Number.isInteger(taskId) || wbsTemplatesLoading}
                >
                  <i className="bi bi-upload" aria-hidden="true" />
                  Import from WBS
                </button>
              ) : null
            ) : null}

            {!isOdcTask ? (
              canForecast ? (
                <button
                  type="button"
                  className="ppc-dd-btn ppc-dd-btn--sm"
                  onClick={openShiftHoursModal}
                  disabled={!Number.isInteger(taskId) || shiftTaskHourForecast.isPending}
                >
                  <i className="bi bi-arrow-left-right" aria-hidden="true" />
                  Shift Hours
                </button>
              ) : null
            ) : null}

            <button
              type="button"
              className="ppc-dd-btn ppc-dd-btn--sm"
              disabled={!Number.isInteger(taskId) || activities.length === 0}
              onClick={() => {
                const firstActivityId = Number(activities?.[0]?.id);
                if (!Number.isInteger(firstActivityId)) return;
                navigateToForecast(firstActivityId);
              }}
            >
              <i className={isOdcTask ? 'bi bi-cash-coin' : 'bi bi-clock'} aria-hidden="true" />
              {isOdcTask ? 'Forecast ODC' : 'Forecast Hours'}
            </button>

            {canManageDisciplines ? (
              <button
                type="button"
                className="ppc-dd-btn ppc-dd-btn--sm ppc-dd-btn--primary ppc-dd-card__add-activity"
                onClick={openAdd}
                disabled={!Number.isInteger(taskId)}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                {`Add ${activityEntitySingular}`}
              </button>
            ) : null}
          </div>
        </div>

        <div className="ppc-dd-tablewrap">
          <table className="ppc-dd-table">
            <thead>
              <tr>
                <th scope="col" className="ppc-dd-table__idx">#</th>
                <th scope="col">{activityEntitySingular}</th>
                <th scope="col">Assumptions</th>
                {!isOdcTask ? <th scope="col">Lead</th> : null}
                {!isOdcTask ? <th scope="col" className="ppc-dd-table__supports-col">Supports</th> : null}
                <th scope="col" className="ppc-dd-table__right">{forecastTotalLabel}</th>
                <th scope="col" className="ppc-dd-table__right">% of Discipline</th>
                {isOdcTask ? <th scope="col" className="ppc-dd-table__right">Actual ($)</th> : null}
                <th scope="col" className="ppc-dd-table__center">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading && activities.length === 0 ? (
                <tr>
                  <td className="ppc-dd-table__empty" colSpan={columnCount}>Loading…</td>
                </tr>
              ) : null}

              {!isLoading && activities.length === 0 ? (
                <tr>
                  <td className="ppc-dd-table__empty" colSpan={columnCount}>
                    {`No ${isOdcTask ? 'items' : 'activities'} yet.`}
                  </td>
                </tr>
              ) : null}

              {activities.map((rowData, rowIndex) => {
                const rowId = Number(rowData?.id);
                const isEditingRow = Number(rowData?.id) === Number(editingId);
                const isEditingSupportsRow = Number(rowData?.id) === Number(editingSupportsActivityId);
                const expanded = Number.isInteger(rowId) ? expandedRowKeys.includes(rowId) : false;

                const supporters = Array.isArray(rowData?.supporters) ? rowData.supporters : [];
                const names = supporters.map((s) => s?.name).filter(Boolean);
                const hasSupports = names.length > 0;
                // Only the first name fits alongside the +N / edit / expand controls on one line.
                const shownNames = names.slice(0, 1);
                const remainingCount = names.length - shownNames.length;

                const forecastValue = isOdcTask
                  ? Number(odcForecastByActivityId.get(rowId) ?? 0)
                  : Number(forecastHoursByActivityId.get(rowId) ?? 0);

                const openRow = () => {
                  if (Number.isInteger(editingId)) return;
                  if (Number.isInteger(editingSupportsActivityId)) return;
                  if (!Number.isInteger(rowId)) return;
                  navigateToForecast(rowId);
                };

                const supportLeadId = Number.isInteger(Number(rowData?.leadUserId)) ? Number(rowData.leadUserId) : null;
                const supportBase = projectMemberOptionsBase.filter((o) => {
                  const id = Number(o?.value);
                  if (!Number.isInteger(id)) return false;
                  if (Number.isInteger(supportLeadId) && id === supportLeadId) return false;
                  return true;
                });
                const inlineSupportsPicker = buildPickerData(
                  supportBase,
                  isEditingSupportsRow ? editingSupportsUserIds : supporters.map((s) => Number(s?.userId))
                );

                return (
                  <React.Fragment key={rowId}>
                    <tr className="ppc-dd-table__row" onClick={openRow}>
                      <td className="ppc-dd-table__idx">{rowIndex + 1}</td>

                      <td
                        className="ppc-dd-table__wrap"
                        style={{ fontSize: getAdaptiveFontSize(rowData?.name) }}
                      >
                        {isEditingRow && editingField === 'name' ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Input
                              as="textarea"
                              rows={4}
                              inputRef={editNameRef}
                              defaultValue={editName}
                              style={{ resize: 'vertical' }}
                              onBlur={() => { void saveEdit(); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') cancelEdit();
                              }}
                            />
                          </div>
                        ) : (
                          <span className="ppc-dd-editable">
                            <span className="ppc-dd-editable__text">{rowData?.name}</span>
                            {canManageDisciplines ? (
                              <button
                                type="button"
                                className="ppc-dd-editable__pencil"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  startEditField(rowData, 'name');
                                }}
                                title={`Edit ${activityEntitySingularLower}`}
                                aria-label={`Edit ${activityEntitySingularLower}`}
                              >
                                <i className="bi bi-pencil" aria-hidden="true" />
                              </button>
                            ) : null}
                          </span>
                        )}
                      </td>

                      <td
                        className="ppc-dd-table__wrap ppc-dd-table__muted"
                        style={{ fontSize: getAdaptiveFontSize(rowData?.assumptions) }}
                      >
                        {isEditingRow && editingField === 'assumptions' ? (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Input
                              as="textarea"
                              rows={4}
                              inputRef={editAssumptionsRef}
                              defaultValue={editAssumptions}
                              style={{ resize: 'vertical' }}
                              onBlur={() => { void saveEdit(); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') cancelEdit();
                              }}
                            />
                          </div>
                        ) : (
                          <span className="ppc-dd-editable">
                            <span className="ppc-dd-editable__text">{rowData?.assumptions || '—'}</span>
                            {canManageDisciplines ? (
                              <button
                                type="button"
                                className="ppc-dd-editable__pencil"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  startEditField(rowData, 'assumptions');
                                }}
                                title="Edit assumptions"
                                aria-label="Edit assumptions"
                              >
                                <i className="bi bi-pencil" aria-hidden="true" />
                              </button>
                            ) : null}
                          </span>
                        )}
                      </td>

                      {!isOdcTask ? (
                        <td style={{ minWidth: 150 }}>
                          {isEditingRow && editingField === 'lead' ? (
                            <div ref={editLeadEditorRef} onClick={(e) => e.stopPropagation()}>
                              <SelectPicker
                                cleanable
                                searchable
                                size="sm"
                                style={{ width: '100%' }}
                                open={leadPickerOpen}
                                placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead'}
                                data={editLeadPicker.data}
                                disabledItemValues={editLeadPicker.disabledItemValues}
                                value={Number.isInteger(editLeadUserId) ? editLeadUserId : null}
                                onOpen={() => setLeadPickerOpen(true)}
                                onChange={(v) => {
                                  const nextLead = Number.isInteger(v) ? v : null;
                                  editLeadUserIdRef.current = nextLead;
                                  setEditLeadUserId(nextLead);
                                  void saveEdit({ leadUserId: nextLead });
                                }}
                                onClose={() => { setLeadPickerOpen(false); }}
                                disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                              />
                            </div>
                          ) : (
                            <span className="ppc-dd-editable">
                              <span className="ppc-dd-editable__text">{rowData?.leadName || '—'}</span>
                              {canManageDisciplines ? (
                                <button
                                  type="button"
                                  className="ppc-dd-editable__pencil"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    startEditField(rowData, 'lead');
                                  }}
                                  title="Edit lead"
                                  aria-label="Edit lead"
                                >
                                  <i className="bi bi-pencil" aria-hidden="true" />
                                </button>
                              ) : null}
                            </span>
                          )}
                        </td>
                      ) : null}

                      {!isOdcTask ? (
                        <td className="ppc-dd-table__supports-col" onClick={(e) => e.stopPropagation()}>
                          <div className="ppc-dd-supports">
                            {isEditingSupportsRow ? (
                              <div style={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
                                <CheckPicker
                                  cleanable
                                  searchable
                                  size="sm"
                                  style={{ width: '100%' }}
                                  open={supportsPickerOpen}
                                  data={inlineSupportsPicker.data}
                                  disabledItemValues={inlineSupportsPicker.disabledItemValues}
                                  value={editingSupportsUserIds}
                                  onOpen={() => setSupportsPickerOpen(true)}
                                  onChange={(values) => {
                                    const next = Array.isArray(values)
                                      ? values.map((v) => Number(v)).filter((v) => Number.isInteger(v))
                                      : [];
                                    editSupportsUserIdsRef.current = next;
                                    setEditingSupportsUserIds(next);
                                  }}
                                  onClose={() => {
                                    setSupportsPickerOpen(false);
                                    void saveSupportsEdit(editSupportsUserIdsRef.current);
                                  }}
                                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0 || setSupporters.isPending}
                                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select supports'}
                                  renderValue={(values, items) => {
                                    // The supports column is narrow, so show one name plus a count instead of
                                    // a comma-joined list that gets clipped mid-name.
                                    const labels = (Array.isArray(items) ? items : [])
                                      .map((it) => it?.label)
                                      .filter((l) => typeof l === 'string' && l.length > 0);
                                    if (labels.length === 0) return null;
                                    const extra = labels.length - 1;
                                    return (
                                      <span className="ppc-dd-picker-value" title={labels.join(', ')}>
                                        <span className="ppc-dd-picker-value__name">{firstNameOf(labels[0])}</span>
                                        {extra > 0 ? <span className="ppc-dd-picker-value__more">{`+${extra}`}</span> : null}
                                      </span>
                                    );
                                  }}
                                />
                              </div>
                            ) : hasSupports ? (
                              <>
                                {shownNames.map((name, idx) => (
                                  <span key={`${name}-${idx}`} className="ppc-dd-chip" title={names.join(', ')}>
                                    {firstNameOf(name)}
                                  </span>
                                ))}
                                {remainingCount > 0 ? (
                                  <span className="ppc-dd-chip ppc-dd-chip--more" title={`${remainingCount} more`}>
                                    {`+${remainingCount}`}
                                  </span>
                                ) : null}
                                {canManageDisciplines ? (
                                  <button
                                    type="button"
                                    className="ppc-dd-minibtn"
                                    onClick={() => openSupportsEdit(rowData)}
                                    title="Edit supports"
                                    aria-label="Edit supports"
                                  >
                                    <i className="bi bi-plus-lg" aria-hidden="true" />
                                  </button>
                                ) : null}
                              </>
                            ) : (
                              canManageDisciplines ? (
                                <button
                                  type="button"
                                  className="ppc-dd-btn ppc-dd-btn--sm"
                                  onClick={() => openSupportsEdit(rowData)}
                                >
                                  Add supports
                                </button>
                              ) : null
                            )}

                            {hasSupports && !isEditingSupportsRow ? (
                              <button
                                type="button"
                                className="ppc-dd-minibtn"
                                onClick={() => toggleExpanded(rowData)}
                                title={expanded ? 'Collapse' : 'Expand'}
                                aria-label={expanded ? 'Collapse support hours' : 'Expand support hours'}
                                aria-expanded={expanded}
                              >
                                <i className={expanded ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} aria-hidden="true" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}

                      <td className="ppc-dd-table__right ppc-dd-num">
                        {isOdcTask ? formatCurrency(forecastValue) : forecastValue.toFixed(2)}
                      </td>

                      <td className="ppc-dd-table__right ppc-dd-num ppc-dd-table__muted">
                        {hasDisciplineTotal ? `${((forecastValue / disciplineTotal) * 100).toFixed(2)}%` : '—'}
                      </td>

                      {isOdcTask ? (
                        <td className="ppc-dd-table__right ppc-dd-num">
                          {formatCurrency(Number(odcActualByActivityId.get(rowId) ?? 0))}
                        </td>
                      ) : null}

                      <td className="ppc-dd-table__center" onClick={(e) => e.stopPropagation()}>
                        <Whisper
                          placement="bottomEnd"
                          trigger="click"
                          container={() => document.body}
                          speaker={(speakerProps, ref) => {
                            const { onClose, className, style, ...rest } = speakerProps;
                            return (
                              <Popover
                                {...rest}
                                ref={ref}
                                className={className}
                                full
                                style={{ ...style, zIndex: 99999 }}
                              >
                                <Menu
                                  onSelect={(eventKey, event) => {
                                    event?.preventDefault?.();
                                    event?.stopPropagation?.();
                                    onClose?.();

                                    if (eventKey === 'hours') navigateToForecast(rowId);
                                    if (eventKey === 'move') openMoveActivityModal(rowData);
                                    if (eventKey === 'delete') openDelete(rowData);
                                  }}
                                >
                                  <Menu.Item eventKey="hours">
                                    {isOdcTask ? 'Forecast ODC' : 'Forecast Hours'}
                                  </Menu.Item>
                                  {canManageDisciplines ? (
                                    <>
                                      <Menu.Item eventKey="move">Move</Menu.Item>
                                      <Menu.Separator />
                                      <Menu.Item eventKey="delete" style={{ color: 'var(--dd-danger)' }}>
                                        Delete {activityEntitySingular}
                                      </Menu.Item>
                                    </>
                                  ) : null}
                                </Menu>
                              </Popover>
                            );
                          }}
                        >
                          <button
                            type="button"
                            className="ppc-dd-minibtn"
                            title="Actions"
                            aria-label="Actions"
                          >
                            <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                          </button>
                        </Whisper>
                      </td>
                    </tr>

                    {expanded && !isOdcTask && names.length > 0 ? (
                      <tr className="ppc-dd-expandrow">
                        <td colSpan={columnCount}>
                          <div className="ppc-dd-expandrow__content">
                            <ActivitySupportHoursExpanded taskId={taskId} activityId={rowId} />
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>

            {activities.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td className="ppc-dd-table__muted">—</td>
                  {!isOdcTask ? <td className="ppc-dd-table__muted">—</td> : null}
                  {!isOdcTask ? <td className="ppc-dd-table__muted">—</td> : null}
                  <td className="ppc-dd-table__right ppc-dd-num">
                    {isOdcTask ? formatCurrency(projectedRevenue) : Number(totalForecastHours).toFixed(2)}
                  </td>
                  <td className="ppc-dd-table__right ppc-dd-num">
                    {hasDisciplineTotal ? '100.00%' : '—'}
                  </td>
                  {isOdcTask ? (
                    <td className="ppc-dd-table__right ppc-dd-num">{formatCurrency(actualRevenue)}</td>
                  ) : null}
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </section>

      {budgetVariance ? (
        <section className="ppc-dd-card">
          <div className="ppc-dd-budget">
            <div className="ppc-dd-budget__row">
              <div>
                <p className="ppc-dd-budget__label">Budget status</p>
                <p className="ppc-dd-budget__caption">Forecast vs allocated budget</p>
              </div>
              {allocatedBudget > 0 ? (
                <p className={`ppc-dd-budget__stat${budgetVariance.isOver ? ' ppc-dd-budget__stat--over' : ''}`}>
                  {budgetVariance.isOver
                    ? `${(budgetUsedPercent - 100).toFixed(2)}% over`
                    : `${(100 - budgetUsedPercent).toFixed(2)}% available`}
                </p>
              ) : null}
            </div>

            {allocatedBudget > 0 ? (
              <div
                className="ppc-dd-budget__track"
                role="progressbar"
                aria-valuenow={Math.round(budgetUsedPercent)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Forecast as a share of the allocated discipline budget"
              >
                <div
                  className={`ppc-dd-budget__fill${budgetVariance.isOver ? ' ppc-dd-budget__fill--over' : ''}`}
                  style={{ width: `${budgetBarPercent}%` }}
                />
              </div>
            ) : null}

            <div className="ppc-dd-budget__row">
              <div>
                <p className="ppc-dd-budget__detail ppc-dd-num">
                  {`Forecast ${formatCurrency(budgetVariance.projected)}`}
                  {isOdcTask ? '' : ` (${Number(totalForecastHours).toFixed(2)} h)`}
                </p>
              </div>
              <span className="ppc-dd-budget__allocated ppc-dd-num">
                {`${formatCurrency(allocatedBudget)} allocated`}
              </span>
            </div>
          </div>
        </section>
      ) : null}

      <div className="ppc-dd-danger-zone">
        {canManageDisciplines ? (
          <button
            type="button"
            className="ppc-dd-btn ppc-dd-btn--danger"
            onClick={() => setDeleteTaskOpen(true)}
            disabled={deleteTaskMutation.isPending || isLoading}
          >
            Delete Discipline
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <Modal
        open={shiftHoursOpen}
        onClose={closeShiftHoursModal} onExited={resetShiftHoursModal}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Shift Hours</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ color: 'var(--rs-text-secondary)', fontSize: 13 }}>
              Adjust the month offset, then confirm to shift all forecast projections for this discipline (across all activities).
            </div>

            {shiftTaskHourForecast.error ? (
              <Message type="error" showIcon>
                {String(shiftTaskHourForecast.error?.message || shiftTaskHourForecast.error)}
              </Message>
            ) : null}

            <Stack spacing={10} justifyContent="center" alignItems="center">
              <Button
                appearance="ghost"
                onClick={() => setShiftHoursDeltaMonths((prev) => Math.max(-24, prev - 1))}
                disabled={shiftTaskHourForecast.isPending || shiftHoursDeltaMonths <= -24}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                  Shift Earlier
                </span>
              </Button>

              <div style={{ minWidth: 220, textAlign: 'center', fontWeight: 700 }}>
                {shiftHoursDeltaMonths === 0
                  ? 'No shift selected'
                  : `${shiftHoursDeltaMonths > 0 ? 'Shift forward' : 'Shift backward'} (${Math.abs(shiftHoursDeltaMonths)}) ${Math.abs(shiftHoursDeltaMonths) === 1 ? 'month' : 'months'}`}
              </div>

              <Button
                appearance="ghost"
                onClick={() => setShiftHoursDeltaMonths((prev) => Math.min(24, prev + 1))}
                disabled={shiftTaskHourForecast.isPending || shiftHoursDeltaMonths >= 24}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  Shift Later
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </span>
              </Button>
            </Stack>

            {shiftPreviewMonthStarts.length > 0 ? (
              <div
                style={{
                  border: '1px solid #e5e5ea',
                  borderRadius: 8,
                  overflowX: 'auto',
                  background: 'var(--rs-bg-card)',
                }}
              >
                <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {shiftPreviewMonthStarts.map((monthStart) => (
                        <th
                          key={`shift-month-${monthStart}`}
                          style={{
                            textAlign: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#555',
                            padding: '8px 6px',
                            borderBottom: '1px solid #e5e5ea',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatShiftPreviewMonthLabel(monthStart)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {shiftPreviewMonthStarts.map((monthStart) => (
                        <td
                          key={`shift-hours-${monthStart}`}
                          style={{
                            textAlign: 'center',
                            padding: '8px 6px',
                            fontSize: 13,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {Number(shiftPreviewHoursByMonth.get(monthStart) ?? 0).toFixed(2)}h
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: '#777', fontSize: 12 }}>No forecast hours found to preview.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeShiftHoursModal} disabled={shiftTaskHourForecast.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void confirmShiftHours()}
            loading={shiftTaskHourForecast.isPending}
            disabled={shiftTaskHourForecast.isPending || !Number.isInteger(shiftHoursDeltaMonths) || shiftHoursDeltaMonths === 0}
          >
            Confirm Shift
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={changeTaskTypeOpen}
        onClose={closeChangeTaskTypeModal}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Change Task Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>Task type</div>
            <SelectPicker
              cleanable={false}
              searchable={false}
              style={{ width: '100%' }}
              data={[
                { label: 'Labor', value: 'labor' },
                { label: 'ODC', value: 'odc' },
              ]}
              value={pendingTaskType}
              onChange={(v) => setPendingTaskType(v === 'odc' ? 'odc' : 'labor')}
            />
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>
              Current: {String(task?.taskType || 'labor').toUpperCase()}
            </div>

            {updateTaskMutation.isError ? (
              <Message type="error" showIcon>
                {String(updateTaskMutation.error?.message || updateTaskMutation.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeChangeTaskTypeModal} disabled={updateTaskMutation.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void submitChangeTaskType()}
            loading={updateTaskMutation.isPending}
            disabled={updateTaskMutation.isPending}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={changeTaskLeadOpen}
        onClose={closeChangeTaskLeadModal}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Change Task Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>Task lead</div>
            <SelectPicker
              cleanable
              searchable
              style={{ width: '100%' }}
              data={taskLeadOptions}
              value={Number.isInteger(Number(pendingTaskLeadUserId)) ? Number(pendingTaskLeadUserId) : null}
              onChange={(v) => setPendingTaskLeadUserId(Number.isInteger(v) ? v : null)}
              placeholder={projectUsersLoading ? 'Loading members…' : 'Select task lead'}
              disabled={projectUsersLoading || taskLeadOptions.length === 0}
            />
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>
              Current: {task?.leadName || '—'}
            </div>

            {updateTaskMutation.isError ? (
              <Message type="error" showIcon>
                {String(updateTaskMutation.error?.message || updateTaskMutation.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeChangeTaskLeadModal} disabled={updateTaskMutation.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => void submitChangeTaskLead()}
            loading={updateTaskMutation.isPending}
            disabled={updateTaskMutation.isPending}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={importWbsOpen}
        onClose={() => {
          if (importWbsPending) return;
          setImportWbsOpen(false);
          setImportTemplateId(null);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Import from WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {wbsTemplatesError ? (
            <Message type="error" showIcon>
              Failed to load WBS templates.
            </Message>
          ) : null}

          {Array.isArray(wbsTemplates) && wbsTemplates.length === 0 ? (
            <Message type="info" showIcon>
              No templates yet. Create one in the WBS Manager.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Template</div>
              <SelectPicker
                cleanable={false}
                searchable
                style={{ width: '100%' }}
                placeholder={wbsTemplatesLoading ? 'Loading templates…' : 'Select a template'}
                data={(wbsTemplates || [])
                  .map((t) => ({ label: t?.name || '—', value: Number(t?.id) }))
                  .filter((o) => Number.isInteger(o.value))}
                value={Number.isInteger(Number(importTemplateId)) ? Number(importTemplateId) : null}
                onChange={(v) => setImportTemplateId(Number.isInteger(v) ? v : null)}
                disabled={wbsTemplatesLoading || (wbsTemplates || []).length === 0}
              />
            </div>

            {importWbsItemsError ? (
              <Message type="error" showIcon>
                Failed to load template items.
              </Message>
            ) : null}

            {Number.isInteger(Number(importTemplateId)) ? (
              <div>
                <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Items</div>
                <div
                  style={{
                    border: '1px solid #eee',
                    borderRadius: 10,
                    padding: 10,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {importWbsItemsLoading ? <div style={{ color: 'var(--rs-text-secondary)' }}>Loading…</div> : null}
                  {!importWbsItemsLoading && (importWbsItems || []).length === 0 ? (
                    <div style={{ color: 'var(--rs-text-secondary)', fontSize: 12 }}>No items in this template.</div>
                  ) : null}
                  <ol style={{ margin: 0, paddingLeft: 18 }}>
                    {(importWbsItems || [])
                      .slice()
                      .sort((a, b) => {
                        const so = Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
                        if (so !== 0) return so;
                        return Number(a?.id ?? 0) - Number(b?.id ?? 0);
                      })
                      .map((i) => (
                        <li key={i.id} style={{ marginBottom: 4 }}>
                          {i.title}
                        </li>
                      ))}
                  </ol>
                </div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              if (importWbsPending) return;
              setImportWbsOpen(false);
              setImportTemplateId(null);
            }}
            disabled={importWbsPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={submitImportWbs}
            loading={importWbsPending}
            disabled={importWbsPending || activities.length !== 0}
          >
            Import
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={addActivityOpen}
        onClose={() => {
          setAddActivityOpen(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Add {activityEntitySingular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>{activityEntitySingular}</div>
              <Input value={newActivityName} onChange={setNewActivityName} placeholder="e.g. Kickoff meeting" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                value={newActivityAssumptions}
                onChange={setNewActivityAssumptions}
                placeholder="Optional"
              />
            </div>

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Lead</div>
                {projectUsersError ? (
                  <Message type="error" showIcon>
                    Failed to load project members.
                  </Message>
                ) : null}
                <SelectPicker
                  className="ppc-discipline-modal-picker"
                  cleanable
                  searchable
                  block
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead (optional)'}
                  style={{ width: '100%' }}
                  data={newActivityLeadPicker.data}
                  disabledItemValues={newActivityLeadPicker.disabledItemValues}
                  value={Number.isInteger(newActivityLeadUserId) ? newActivityLeadUserId : null}
                  onChange={(v) => {
                    const nextLead = Number.isInteger(v) ? v : null;
                    setNewActivityLeadUserId(nextLead);
                    if (Number.isInteger(nextLead)) {
                      setNewActivitySupportUserIds((prev) =>
                        (Array.isArray(prev) ? prev : []).filter((id) => Number(id) !== Number(nextLead))
                      );
                    }
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
              </div>
            ) : null}

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Supports</div>
                <CheckPicker
                  className="ppc-discipline-modal-picker"
                  searchable
                  ref={newActivitySupportsPickerRef}
                  block
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select support(s) (optional)'}
                  style={{ width: '100%' }}
                  data={newActivitySupportsPicker.data}
                  disabledItemValues={newActivitySupportsPicker.disabledItemValues}
                  value={Array.isArray(newActivitySupportUserIds) ? newActivitySupportUserIds : []}
                  onChange={(v) => {
                    setNewActivitySupportUserIds(Array.isArray(v) ? v : []);
                  }}
                  onSelect={() => {
                    // Clear the internal search box after Enter-to-select (keeps menu open for fast multi-add).
                    clearRsuitePickerSearchInputSoon(newActivitySupportsPickerRef);
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
                <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>Lead is excluded from this list.</div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddActivityOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitAdd} loading={createActivity.isPending}>
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={editTaskOpen}
        onClose={() => {
          if (updateActivity.isPending || setSupporters.isPending) return;
          setEditTaskOpen(false);
          setEditTaskActivity(null);
          setEditTaskName('');
          setEditTaskAssumptions('');
          setEditTaskLeadUserId(null);
          setEditTaskSupportUserIds([]);
          setConfirmTeamRemovalOpen(false);
          setConfirmRemovedUserIds([]);
          setEditTaskPendingSave(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Edit Discipline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>
              {activityEntitySingular}: <span style={{ fontWeight: 600 }}>{editTaskActivity?.name || ''}</span>
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Name</div>
              <Input value={editTaskName} onChange={setEditTaskName} placeholder="e.g. Kickoff meeting" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                value={editTaskAssumptions}
                onChange={setEditTaskAssumptions}
                placeholder="Optional"
              />
            </div>

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Lead</div>
                {projectUsersError ? (
                  <Message type="error" showIcon>
                    Failed to load project members.
                  </Message>
                ) : null}
                <SelectPicker
                  className="ppc-discipline-modal-picker"
                  ref={editTaskLeadPickerRef}
                  cleanable
                  searchable
                  block
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead (optional)'}
                  style={{ width: '100%' }}
                  data={editTaskLeadPicker.data}
                  disabledItemValues={editTaskLeadPicker.disabledItemValues}
                  value={Number.isInteger(editTaskLeadUserId) ? editTaskLeadUserId : null}
                  onOpen={() => {
                    clearRsuitePickerSearchInputSoon(editTaskLeadPickerRef);
                  }}
                  onSelect={() => {
                    clearRsuitePickerSearchInputSoon(editTaskLeadPickerRef);
                  }}
                  onChange={(v) => {
                    const nextLead = Number.isInteger(v) ? v : null;
                    setEditTaskLeadUserId(nextLead);
                    if (Number.isInteger(nextLead)) {
                      setEditTaskSupportUserIds((prev) =>
                        (Array.isArray(prev) ? prev : []).filter((id) => Number(id) !== Number(nextLead))
                      );
                    }
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
              </div>
            ) : null}

            {!isOdcTask ? (
              <div>
                <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Supports</div>
                <CheckPicker
                  className="ppc-discipline-modal-picker"
                  searchable
                  ref={editTaskSupportsPickerRef}
                  block
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select support(s) (optional)'}
                  style={{ width: '100%' }}
                  data={editTaskSupportsPicker.data}
                  disabledItemValues={editTaskSupportsPicker.disabledItemValues}
                  value={Array.isArray(editTaskSupportUserIds) ? editTaskSupportUserIds : []}
                  onChange={(v) => {
                    const leadId = Number.isInteger(editTaskLeadUserId) ? Number(editTaskLeadUserId) : null;
                    const next = Array.isArray(v) ? v : [];
                    const filtered = Number.isInteger(leadId)
                      ? next.filter((id) => Number(id) !== leadId)
                      : next;
                    setEditTaskSupportUserIds(filtered);
                  }}
                  onSelect={() => {
                    clearRsuitePickerSearchInputSoon(editTaskSupportsPickerRef);
                  }}
                  disabled={projectUsersLoading || projectMemberOptionsBase.length === 0}
                />
                <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>Lead is excluded from this list.</div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              if (updateActivity.isPending || setSupporters.isPending) return;
              setEditTaskOpen(false);
              setEditTaskActivity(null);
              setEditTaskName('');
              setEditTaskAssumptions('');
              setEditTaskLeadUserId(null);
              setEditTaskSupportUserIds([]);
              setConfirmTeamRemovalOpen(false);
              setConfirmRemovedUserIds([]);
              setEditTaskPendingSave(false);
            }}
            disabled={updateActivity.isPending || setSupporters.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => submitEditTask({ skipConfirm: false })}
            loading={updateActivity.isPending || setSupporters.isPending}
            disabled={updateActivity.isPending || setSupporters.isPending}
          >
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmTeamRemovalOpen}
        onClose={() => {
          setConfirmTeamRemovalOpen(false);
          setEditTaskPendingSave(false);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Confirm removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            Removing a lead/support will clear their hours for this {activityEntitySingularLower}.
          </Message>

          {confirmRemovedUserIds.length > 0 ? (
            <div style={{ marginTop: 10, fontSize: 13 }}>
              You are removing:
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                {confirmRemovedUserIds
                  .slice()
                  .map((id) => ({ id, name: getDisplayNameForUserId(id) }))
                  .sort((a, b) => String(a.name).localeCompare(String(b.name)))
                  .map((u) => (
                    <li key={u.id}>{u.name}</li>
                  ))}
              </ul>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              setConfirmTeamRemovalOpen(false);
              setEditTaskPendingSave(false);
            }}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            onClick={() => {
              setConfirmTeamRemovalOpen(false);
              if (!editTaskPendingSave) return;
              submitEditTask({ skipConfirm: true });
            }}
          >
            Remove & Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={moveActivityOpen}
        onClose={closeMoveActivityModal}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Move to new discipline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)' }}>
              {activityEntitySingular}: <span style={{ fontWeight: 600 }}>{String(moveActivity?.name || '—')}</span>
            </div>

            {projectTasksError ? (
              <Message type="error" showIcon>
                Failed to load project disciplines.
              </Message>
            ) : null}

            <div>
              <div style={{ fontSize: 12, color: 'var(--rs-text-secondary)', marginBottom: 6 }}>Discipline</div>
              <SelectPicker
                cleanable={false}
                searchable
                block
                placeholder={projectTasksLoading ? 'Loading disciplines…' : 'Select a discipline'}
                data={moveDisciplineOptions}
                value={Number.isInteger(Number(moveTargetTaskId)) ? Number(moveTargetTaskId) : null}
                onChange={(v) => setMoveTargetTaskId(Number.isInteger(Number(v)) ? Number(v) : null)}
                disabled={projectTasksLoading || moveDisciplineOptions.length === 0}
              />
            </div>

            {!projectTasksLoading && moveDisciplineOptions.length === 0 ? (
              <Message type="info" showIcon>
                No other disciplines are available in this project.
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeMoveActivityModal} disabled={updateActivity.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={() => {
              void submitMoveActivity();
            }}
            loading={updateActivity.isPending}
            disabled={updateActivity.isPending || !Number.isInteger(Number(moveTargetTaskId))}
          >
            Move
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteActivity(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete {activityEntitySingular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This cannot be undone.
          </Message>
          <div style={{ marginTop: 10, fontSize: 13 }}>
            Delete <b>{deleteActivity?.name || `this ${activityEntitySingularLower}`}</b>?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              setDeleteOpen(false);
              setDeleteActivity(null);
            }}
            disabled={deleteActivityMutation.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton variant="danger-solid" onClick={submitDelete} loading={deleteActivityMutation.isPending}>
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={deleteTaskOpen}
        onClose={() => {
          if (deleteTaskMutation.isPending) return;
          setDeleteTaskOpen(false);
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete discipline?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will delete the discipline and all {isOdcTask ? 'items' : 'activities'} under it.
          </Message>
          {deleteTaskMutation.isError ? (
            <Message type="error" showIcon style={{ marginTop: 10 }}>
              {String(deleteTaskMutation.error?.message || deleteTaskMutation.error)}
            </Message>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => setDeleteTaskOpen(false)}
            disabled={deleteTaskMutation.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            onClick={submitDeleteTask}
            loading={deleteTaskMutation.isPending}
            disabled={deleteTaskMutation.isPending}
          >
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
